<template>
    <div class="video-player-card">
        <div class="video-content">
            <!-- Shimmer while video is loading -->
            <div v-if="!videoBlobUrl" class="video-shimmer-wrapper">
                <div class="shimmer shimmer-video"></div>
            </div>

            <!-- Native video player -->
            <video v-else :src="videoBlobUrl" controls controlsList="nodownload" class="video-element"
                @canplay="onVideoCanPlay" @waiting="onVideoWaiting" @loadstart="onVideoLoadStart"
                @seeking="onVideoSeeking" @pause="updateWatchTimeHandler"></video>
            <!-- YouTube video player -->
            <div v-if="!isVideo && !loadingVideo" class="youtube-embed-wrapper">
                <div :id="youtubePlayerId" :key="youtubePlayerKey" class="youtube-iframe"></div>
            </div>
        </div>

        <!-- Video metadata section (optional to show) -->
        <div class="video-info">
            <div class="row">
                <div class="d-block mb-2 mb-lg-0 d-lg-none col-12 justify-content-start">
                    <button class="btn btn-outline-primary btn-sm" @click="goToCourse"> <i
                            class="fa fa-arrow-left me-2"></i> Back To Course </button>
                </div>
                <div class="col-12 col-lg-8">
                    <h2 class="video-title">{{ title }}</h2>
                </div>
                <div class="d-none d-lg-block col-lg-4">
                    <button class="btn btn-outline-primary btn-sm" @click="goToCourse"> <i
                            class="fa fa-arrow-left me-2"></i> Back To Course </button>
                </div>
                <div class="col-12 col-lg-6 mt-md-2">
                    <div class="video-meta">
                        <span class="badge badge-category">{{ category }}</span>
                        <span class="badge badge-level">{{ level }}</span>
                        <span class="badge badge-duration">
                            <i class="far fa-clock"></i> {{ duration }} min
                        </span>
                    </div>
                </div>
                <div class="col-12 col-lg-6 d-flex justify-content-lg-end justify-content-start mb-2 mb-lg-0">
                    <div class="w-100" style="max-width: 300px;">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="text-muted small">Progress</span>
                            <span class="text-muted small">{{ progress }}%</span>
                        </div>
                        <div class="progress" style="height: 8px;">
                            <div class="progress-bar" :style="{ width: progress + '%' }" :aria-valuenow="progress"
                                aria-valuemin="0" aria-valuemax="100">
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- <p class="video-description mt-4 mt-md-0">{{ description }}</p> -->

            <p class="video-description">
                <span v-html="isExpanded ? description : shortDescription"></span>
                <span v-if="shortDescription && shortDescription !== description">
                    <button @click="isExpanded = !isExpanded" class="btn btn-link p-0 align-baseline"
                        style="font-size: inherit;">
                        {{ isExpanded ? ' Show Less' : ' Show More' }}
                    </button>
                </span>
            </p>

            <div class="d-flex justify-content-between mt-3">
                <button class="btn btn-outline-primary btn-sm" @click="goToPrevious" :disabled="!hasPreviousLesson">
                    <i class="fa fa-arrow-left me-2"></i> Previous
                </button>
                <button class="btn btn-outline-primary btn-sm" @click="goToNext" :disabled="!hasNextLesson">
                    Next <i class="fa fa-arrow-right ms-2"></i>
                </button>
            </div>
        </div>
    </div>
</template>


<script>
export default {
    name: 'VideoPlayer',
    props: {
        isVideo: { type: Boolean, required: true }, // True if uploaded video, false if YouTube
        videoUrl: { type: String, required: true }, // Video file URL or YouTube URL
        title: { type: String, required: true }, // Course title
        description: { type: String, required: true }, // Course description
        thumbnail: { type: String, required: true }, // Thumbnail image URL
        level: { type: String, required: true }, // Course level
        category: { type: String, required: true }, // Course category
        duration: { type: [String, Number], required: false, default: 0 }, // Duration in minutes (or seconds)
        visible: { type: Boolean, required: true }, // Controls modal visibility
        modalMode: { type: String, default: 'regular' }, // Modal display mode
        courseId: { type: [String, Number], required: true },
        progress: { type: [Number, String], default: 0 }, // Progress percentage
        shortDescription: { type: String, required: false },
        showFullDescription: { type: Boolean, default: false },
        previousWatchedTime: { type: Number, default: 0 }, // Previous watched time in seconds
        description: String,
        videoLessonId: { type: [String, Number], required: true },
        hasNextLesson: { type: Boolean, default: false }, // If there is a next lesson
        hasPreviousLesson: { type: Boolean, default: false }, // If there is a previous lesson
    },
    data() {
        return {
            playing: false, // True if video is playing (after play button click)
            videoBlobUrl: null,
            loadingVideo: true,
            thumbnailBlobUrl: null,
            hasStarted: false,
            youtubePlayer: null,
            youtubePlayerId: 'youtube-player-' + Math.random().toString(36).substr(2, 9),
            youtubePlayerKey: Date.now(),
            shouldAutoPlayYouTube: false, // Flag to auto-play YouTube on ready
            videoProgressStatus: '',
            hasSetStartTime: false,
            maxWatchedTime: this.previousWatchedTime,
            isExpanded: false,
        };
    },
    methods: {
        /* ------------------------------ VIDEO EVENTS ------------------------------ */

        onVideoSeeking() {
            const videoEl = this.$el.querySelector('.video-element');
            if (!videoEl) return;
            // Only restrict if not completed
            if (this.videoProgressStatus !== 'completed') {
                if (videoEl.currentTime > this.previousWatchedTime) {
                    videoEl.currentTime = this.previousWatchedTime;
                }
            }
        },

        onVideoCanPlay() {
            this.loadingVideo = false;
            const videoEl = this.$el.querySelector('.video-element');
            if (!videoEl) return;

            if ((videoEl && !this.hasSetStartTime) && (this.previousWatchedTime && (this.videoProgressStatus !== 'completed' && this.videoProgressStatus == 'in progress'))) {
                videoEl.currentTime = this.previousWatchedTime;
                this.hasSetStartTime = true;
            }

            //Autoplay video if allowed
            if (this.playing) {
                videoEl.play().catch((err) => {
                    console.warn('Autoplay blocked:', err);
                });
            }
        },

        onVideoWaiting() {
            this.loadingVideo = true;
        },

        onVideoLoadStart() {
            this.loadingVideo = true;
        },

        /* -------------------------- VIDEO PLAYBACK CONTROL ------------------------- */
        async playVideo() {
            if (!this.hasStarted) {
                try {
                    await axios.post('/staff/learnings/start', {
                        course_id: this.courseId,
                        video_lesson_id: this.videoLessonId
                    });
                    this.hasStarted = true;
                } catch (e) { }
            }
            if (!this.isVideo) {
                this.shouldAutoPlayYouTube = true;
                this.resetYouTubePlayerKeyAndId();
            }
            this.playing = true;
        },

        async updateWatchTimeHandler() {
            let watchedTime = 0;
            let progressStatus = null;

            if (this.isVideo) {
                const videoEl = this.$el.querySelector('.video-element');
                if (videoEl && videoEl.seeking) {
                    return;
                }
                if (videoEl) {
                    watchedTime = Math.floor(videoEl.currentTime);
                }
            } else if (this.youtubePlayer && typeof this.youtubePlayer.getCurrentTime === 'function') {
                watchedTime = Math.floor(this.youtubePlayer.getCurrentTime());
            }

            if (watchedTime > this.previousWatchedTime) {
                try {
                    const response = await this.updateProgressRequest(watchedTime);
                    if (response.data.status) {
                        progressStatus = response.data.status;
                        this.$emit('progress-updated', { courseId: this.courseId, status: progressStatus, watchTime: watchedTime, progressPercent: response.data.progressPercent });
                    }
                } catch (e) {
                    console.error('Progress update failed:', e);
                }
            }
        },
        /* ------------=---------------- YOUTUBE CONTROL ---------------------------- */

        resetYouTubePlayerKeyAndId() {
            this.youtubePlayerKey = Date.now();
            this.youtubePlayerId ='youtube-player-' + Math.random().toString(36).substr(2, 9);
        },

        initYouTubePlayer() {
            if (this.youtubePlayer) return; // Prevent multiple initializations
            if (window.YT && window.YT.Player) {
                this.createYouTubePlayer();
            } else {
                // If API is not loaded, set callback
                window.onYouTubeIframeAPIReady = this.createYouTubePlayer;
                if (!document.getElementById('youtube-iframe-api')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    tag.id = 'youtube-iframe-api';
                    document.body.appendChild(tag);
                }
            }
        },


        createYouTubePlayer() {
            if (this.youtubePlayer) {
                this.youtubePlayer.destroy();
                this.youtubePlayer = null;
            }
            this.youtubePlayer = new window.YT.Player(this.youtubePlayerId, {
                videoId: this.extractYouTubeId(this.videoUrl),
                events: {
                    onReady: (event) => {
                        if (this.shouldAutoPlayYouTube && this.playing) {
                            if (this.videoProgressStatus === 'in progress' && this.previousWatchedTime) {
                                event.target.seekTo(this.previousWatchedTime, true);
                            }
                            event.target.playVideo();
                            this.shouldAutoPlayYouTube = false;
                        }
                        // Start seek lock if in progress
                        this.startYouTubeSeekLock();
                    },
                    onStateChange: async (event) => {
                        // console.log('YoutubeEvents:', event.data);
                        const yt = window.YT;
                        if (event.data === yt.PlayerState.PLAYING) {
                            this.startYouTubeSeekLock();
                        } else if (event.data === yt.PlayerState.PAUSED) {
                            await this.updateWatchTimeHandler();
                            this.startYouTubeSeekLock();
                        } else if (event.data === yt.PlayerState.ENDED) {
                            await this.updateWatchTimeHandler();
                        }
                    },
                }
            });
        },

        startYouTubeSeekLock() {
            if (this.videoProgressStatus === 'completed') return; // Only lock if in progress

            const yt = this.youtubePlayer;
            if (!yt || typeof yt.getCurrentTime !== 'function') return;
            const currentTime = yt.getCurrentTime();
            this.maxWatchedTime = this.previousWatchedTime;
            // // If user seeks beyond the allowed limit, rewind them
            if (currentTime > this.maxWatchedTime + 1) {
                yt.seekTo(this.maxWatchedTime, true);
            }
        },

        destroyYouTubePlayer() {
            if (this.youtubePlayer) {

                this.youtubePlayer.destroy();
                this.youtubePlayer = null;
            }

            if (!this.isVideo) {
                this.resetYouTubePlayerKeyAndId();
            }
        },
        /* ---------------------------- Axio Calls ---------------------------- */

        async fetchProgressStatus() {
            try {
                const response = await axios.get(`/staff/learnings/progress-status/${this.videoLessonId}`);
                if (response.data && response.data.is_progress) {
                    this.hasStarted = true;
                    this.videoProgressStatus = response.data.status;
                }
            } catch (e) {
                this.hasStarted = false;
                // Optionally handle error
            } finally {
            }
        },

        async fetchVideoWithAuth() {
            this.revokeBlob();
            try {
                const response = await axios.get(`/training-courses/video-lessons/video/${this.videoUrl}`, {
                    responseType: 'blob'
                });
                const blob = response.data;
                this.videoBlobUrl = URL.createObjectURL(blob);
                // console.log('Video URL:', this.videoBlobUrl);
            } catch (e) {
                this.videoBlobUrl = null;
                this.loadingVideo = false;
                // Optionally show error
            }
        },

        async updateProgressRequest(watchTime) {
            try {
                if (this.videoProgressStatus === 'completed') {
                    return;
                }

                const requestResponse = await axios.post('/staff/learnings/update-progress', {
                    course_id: this.courseId,
                    video_lesson_id: this.videoLessonId,
                    watch_time: watchTime
                });

                return requestResponse;

            } catch (e) {
                throw e;
            }
        },

        /* ---------------------------- Utilities ---------------------------- */

        setMode(mode) {
            this.$emit('update:modalMode', mode);
        },

        revokeBlob() {
            if (this.videoBlobUrl) {
                URL.revokeObjectURL(this.videoBlobUrl);
                this.videoBlobUrl = null;
            }
        },

        extractYouTubeId(url) {
            // Extracts the video ID from a YouTube URL
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            return (match && match[2].length === 11) ? match[2] : null;
        },

        async goToPrevious() {
            await this.updateWatchTimeHandler();
            this.destroyYouTubePlayer();
            this.$emit('previous-click');
        },

        async goToNext() {
            await this.updateWatchTimeHandler();
            this.destroyYouTubePlayer();
            this.$emit('next-click');
        },

        async goToCourse() {
            await this.updateWatchTimeHandler();
            this.destroyYouTubePlayer();
            this.$router.push({ name: 'staff.training-courses.course-detail', params: { courseId: this.courseId } });
        }
    },

    /* ---------------------------- REACTIVE WATCHERS ---------------------------- */
    watch: {
        async youtubePlayerKey() {
            if (!this.isVideo && this.playing) {
                await this.fetchProgressStatus();
                this.$nextTick(() => {
                    this.initYouTubePlayer();
                });
            }
        },
        playing(val) {
            if (val && this.isVideo) {
                this.fetchVideoWithAuth();
            }
        },
        async visible(val) {
            if (val) {
                await this.fetchProgressStatus();
                this.$nextTick(() => {
                    this.initYouTubePlayer();
                });
            } else {
                this.revokeBlob();
                this.playing = false;
                this.destroyYouTubePlayer();
            }
        },
        previousWatchedTime(newVal) {
            this.maxWatchedTime = newVal;
        },
        description() {
            this.isExpanded = false;
        },
    },
    /* ------------------------------ LIFECYCLE HOOK ----------------------------- */
    async mounted() {
        await this.fetchProgressStatus();
        await this.playVideo();
        this.loadingVideo = false;
    },
};
</script>
